// The ONLY file in the repo allowed to import @statsig/* (enforced by the eslint fence in package.json).
import React, { useCallback, useEffect, useMemo } from "react";
import {
  StatsigProvider,
  useClientAsyncInit,
  useStatsigClient,
} from "@statsig/react-bindings";
import { registerChecker } from "../activeFlags";
import { FeatureFlagContext } from "../context";
import { defaultFor } from "../registry";

const STATSIG_TIERS = {
  local: "development",
  staging: "staging",
  production: "production",
};

export const tierFor = (environment) => STATSIG_TIERS[environment];

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Sits inside StatsigProvider so values_updated re-renders it; bridges the
// native context into our vendor-free FeatureFlagContext.
const FlagBridge = ({ isLoading, children }) => {
  const { checkGate } = useStatsigClient();

  const isFeatureActive = useCallback(
    (flagName) => {
      try {
        return isLoading ? defaultFor(flagName) : checkGate(flagName);
      } catch {
        return defaultFor(flagName);
      }
    },
    [checkGate, isLoading],
  );

  useEffect(() => {
    registerChecker(isFeatureActive);
    return () => registerChecker(null);
  }, [isFeatureActive]);

  const value = useMemo(
    () => ({ isFeatureActive, isReady: !isLoading }),
    [isFeatureActive, isLoading],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const StatsigFlagProvider = ({ sdkKey, environment, user, children }) => {
  const { client, isLoading } = useClientAsyncInit(sdkKey, user(), {
    environment: { tier: tierFor(environment) },
  });

  useEffect(() => {
    const refresh = () => client.updateUserAsync(user()).catch(() => {});
    // The bindings reuse one client per sdkKey across remounts; re-sync the
    // identity in case a different user signed in since the client was built.
    if (client.loadingStatus === "Ready") refresh();
    window.addEventListener("focus", refresh);
    const intervalId = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => {
      window.removeEventListener("focus", refresh);
      clearInterval(intervalId);
    };
  }, [client, user]);

  return (
    <StatsigProvider client={client}>
      <FlagBridge isLoading={isLoading}>{children}</FlagBridge>
    </StatsigProvider>
  );
};
